import { useEffect, useState } from 'react'
import { api, type Auswertung, type TagesBild } from '../api'
import { anzahl, datumLang, euro, euroKurz, menge, prozent } from '../format'
import { Ampel } from '../komponenten/Ampel'
import { Anteile } from '../komponenten/Anteile'
import { Balken } from '../komponenten/Balken'
import { stundenLuecken } from '../komponenten/diagramm'
import { Karte } from '../komponenten/Karte'
import { Kennzahl } from '../komponenten/Kennzahl'
import { Rangliste } from '../komponenten/Rangliste'
import { Zahlarten } from '../komponenten/Zahlarten'
import { SpeisenGetraenke } from '../komponenten/SpeisenGetraenke'
import { Auswertungsraster } from '../komponenten/Auswertungsraster'
import { Tipp } from '../komponenten/Hinweis'

export function Tag({ datum, betriebe }: { datum?: string; betriebe: string[] }) {
  const [d, setD] = useState<TagesBild | null>(null)
  const [kacheln, setKacheln] = useState<Auswertung[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(true)

  useEffect(() => {
    let weg = false
    setLaedt(true); setFehler(null)
    api.auswertungen('tag')
      .then(async (liste) => {
        const aktiv = liste.filter((k) => k.aktiv).map((k) => k.schluessel)
        const bild = await api.tag(datum, betriebe, aktiv)
        if (!weg) { setKacheln(liste); setD(bild) }
      })
      .catch((e) => { if (!weg) setFehler(e.message) })
      .finally(() => { if (!weg) setLaedt(false) })
    return () => { weg = true }
  }, [datum, betriebe.join(',')])

  if (fehler) return <div className="fehler">{fehler}</div>
  if (laedt && !d) return <div className="laedt">Wird geladen …</div>
  if (!d) return null

  const k = d.kennzahlen
  const v = d.vergleich
  const s = d.stornos
  const zustand = d.ampel?.[0]?.ampel ?? null

  const inhalt = {
    'tag.umsatz': () => (
        <Kennzahl etikett={<>Gesamtumsatz <Tipp text="Umsatz vor Rabatt, ohne Trinkgeld. Bei teilweise rabattierten Rechnungen zählt auch der rabattierte Anteil mit." /></>}
                  wert={euro(k!.umsatz)}
                  veraenderung={v!.delta_vorwoche}
                  verglichenMit="zum selben Wochentag" />
    ),
    'tag.umsatz_bereinigt': () => (
        <Kennzahl
          etikett={<>Umsatz bereinigt <Tipp text={`Gesamtumsatz der Kasse ohne Verlust/Bruch (${euro(k!.bereinigung_bruch)}) und Mitarbeiteressen (${euro(k!.bereinigung_mitarbeiter)}). Trinkgeld ist in keiner der beiden Umsatzgrößen enthalten.`} /></>}
          wert={euro(k!.umsatz_bereinigt)} />
    ),
    'tag.rechnungen': () => (
      <>
        {/* Die Zweitzeile nur, wenn sie etwas anderes sagt. Wo kein Tisch
            geteilt wurde, sind Rechnungen und Tischbelegungen dieselbe Zahl,
            und sie zweimal zu nennen sieht nach einem Fehler aus. */}
        <Kennzahl etikett="Rechnungen" wert={anzahl(k!.rechnungen)}
                  fuss={k!.tischbesuche !== k!.rechnungen
                    ? `auf ${anzahl(k!.tischbesuche)} Tischbelegungen` : undefined} />
      </>
    ),
    'tag.durchschnittsrechnung': () => (
        <Kennzahl
          etikett={<>Durchschnittsrechnung <Tipp text={`${euro(k!.umsatz)} Gesamtumsatz vor Rabatt geteilt durch ${anzahl(k!.rechnungen)} Rechnungen. Trinkgeld ist nicht enthalten.`} /></>}
          wert={euro(k!.durchschnittsbon)}
                  fuss={k!.tischbesuche !== k!.rechnungen
                    ? `${euro(k!.umsatz_je_tischbesuch)} je Tischbelegung` : undefined} />
    ),
    'tag.trinkgeld': () => (
        <Kennzahl etikett="Trinkgeld" wert={euro(k!.trinkgeld)}
                  fuss={k!.trinkgeldquote !== null
                    ? `${prozent(k!.trinkgeldquote)} vom Umsatz` : undefined} />
    ),
    'tag.umsatz_stunden': () => (
        <Karte titel="Umsatz im Tagesverlauf"
               neben="nach Abschlusszeit, Ortszeit des Standorts">
          {d.stunden!.length ? (
            <Balken
              werte={stundenLuecken(d.stunden!,
                (h) => ({ stunde: h, umsatz: '0', rechnungen: 0 }),
              ).map((h) => ({
                schluessel: String(h.stunde),
                etikett: String(h.stunde).padStart(2, '0'),
                wert: Number(h.umsatz),
                neben: anzahl(h.rechnungen),
              }))}
              formatiere={euro} formatiereAchse={euroKurz} />
          ) : <div className="leer">Keine Belege an diesem Tag.</div>}
        </Karte>
    ),
    'tag.zahlarten': () => (
        <Karte titel="Zahlarten" neben="nach Art und Namen der Kasse">
          <Zahlarten zeilen={d.zahlarten_umsatz!} gutscheine={d.gutscheine!}
                     berichtVorhanden={d.zahlungsbericht_vorhanden!} />
        </Karte>
    ),
    'tag.speisen_getraenke': () => <SpeisenGetraenke d={d.speisen_getraenke!} />,
    'tag.renner': () => (
        <Karte titel="Renner" neben="nach Umsatz, netto nach Storno">
          <Rangliste zeilen={d.renner!} />
        </Karte>
    ),
    'tag.penner': () => (
        <Karte titel="Penner" neben="verkauft, aber am seltensten">
          <Rangliste zeilen={d.penner!} nach="menge" />
        </Karte>
    ),
    'tag.warengruppen': () => (
        <Karte titel="Warengruppen">
          {d.warengruppen!.length ? (
            <>
              <Anteile
                anteile={d.warengruppen!.map((w) => ({
                  name: w.warengruppe, wert: Number(w.umsatz),
                }))}
                formatiere={euro} />
              <table style={{ marginTop: 16 }}>
                <thead>
                  <tr>
                    <th>Warengruppe</th>
                    <th className="r">Menge</th>
                    <th className="r">Positionen</th>
                    <th className="r">Umsatz</th>
                  </tr>
                </thead>
                <tbody>
                  {d.warengruppen!.map((w) => (
                    <tr key={w.warengruppe}>
                      <td>{w.warengruppe}</td>
                      <td className="r zahl">{menge(w.menge)}</td>
                      <td className="r zahl dezent">{anzahl(w.positionen)}</td>
                      <td className="r zahl">{euro(w.umsatz)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : <div className="leer">Keine Warengruppen.</div>}
        </Karte>
    ),
    'tag.abgleich': () => (
          <Karte titel="Abgleich" neben={datumLang(d.tag)}>
            <div style={{ marginBottom: 10 }}><Ampel zustand={zustand} /></div>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--schrift-2)', lineHeight: 1.55 }}>
              {zustand === 'GREEN'
                ? 'Alle Prüfungen gegen den Kassenabschluss bestanden. Die Zahlen dieses Tages sind belastbar.'
                : zustand === 'AMBER'
                ? 'Eine Angabe der Kasse über sich selbst ist unstimmig. Die Summen stimmen — es fehlt kein Geld.'
                : zustand === 'RED'
                ? 'Eine Summenprüfung weicht ab. Dieser Tag geht nicht in die Kennzahlen ein, bis der Befund geklärt ist.'
                : 'Für diesen Tag liegt noch kein Abgleich vor.'}
            </p>
            {d.tagesabschluss && d.tagesabschluss.erwartet > 0 && (
              <p className="dezent" style={{ margin: '10px 0 0', fontSize: 11 }}>
                Tagesabschlussbericht: {d.tagesabschluss.vorhanden === d.tagesabschluss.erwartet
                  ? 'liegt vor'
                  : d.tagesabschluss.erwartet === 1
                    ? 'liegt nicht vor'
                    : `${d.tagesabschluss.vorhanden} von ${d.tagesabschluss.erwartet} Standorten`}
                {' · '}Gegenprobe ohne Einfluss auf die Ampel
              </p>
            )}
          </Karte>
    ),
    'tag.stornos_rabatte': () => (
          <Karte titel="Stornos und Rabatte">
            <table>
              <tbody>
                <tr>
                  <td>
                    Zurückgenommen
                    <div className="dezent" style={{ fontSize: 11, marginTop: 2 }}>
                      Zahlungen, die nach dem Abschluss zurückgenommen wurden
                    </div>
                    <div className="dezent" style={{ fontSize: 10, marginTop: 2 }}>
                      entspricht der Spalte „Storniert“ im Quittungsbericht des Backoffice
                    </div>
                  </td>
                  <td className="r zahl dezent">{anzahl(s!.zurueckgenommen_anzahl)}×</td>
                  <td className="r zahl">{euro(s!.zurueckgenommen_summe)}</td>
                </tr>
                <tr>
                  <td>
                    Stornoquote
                    <div className="dezent" style={{ fontSize: 11, marginTop: 2 }}>
                      Positionen, die von einer Rechnung genommen wurden
                    </div>
                  </td>
                  <td className="r dezent" style={{ fontSize: 11 }}>
                    {s!.stornoquote === null
                      ? 'braucht den Transaktionsbericht'
                      : 'vom gebuchten Umsatz'}
                  </td>
                  <td className="r zahl">
                    {s!.stornoquote === null
                      ? <span className="dezent" style={{ fontSize: 11 }}>
                          nicht auswertbar
                        </span>
                      : prozent(s!.stornoquote)}
                  </td>
                </tr>
                <tr>
                  <td>Gesamtrabatt</td>
                  <td className="r zahl dezent">{anzahl(s!.rabatt_anzahl)}×</td>
                  <td className="r zahl">{euro(s!.rabatt_gesamt)}</td>
                </tr>
                {s!.rabatt_toepfe && (
                  <>
                    <tr><td>Rabatte der Kasse</td><td /><td className="r zahl">{euro(s!.rabatt_toepfe.rabatte)}</td></tr>
                    <tr><td>Maison (100 %)</td><td /><td className="r zahl">{euro(s!.rabatt_toepfe.maison)}</td></tr>
                    <tr><td>Verlust/Bruch</td><td /><td className="r zahl">{euro(s!.rabatt_toepfe.verlust_bruch)}</td></tr>
                  </>
                )}
                <tr>
                  <td>
                    Sofortstornos
                    <div className="dezent" style={{ fontSize: 11, marginTop: 2 }}>
                      tragen 0,00 € bei — beziffert sind die Einzelpreise
                    </div>
                  </td>
                  <td className="r zahl dezent">{anzahl(s!.sofort_anzahl)}×</td>
                  <td className="r zahl">{euro(s!.sofort_wert)}</td>
                </tr>
              </tbody>
            </table>
            {s!.rabatt_toepfe && Number(s!.rabatt_toepfe.abweichung) !== 0 && (
              <p className="dezent">Die Aufteilung des Quittungsberichts erklärt die Rabatt-Gesamtsumme nicht vollständig ({euro(s!.rabatt_toepfe.abweichung)} Differenz).</p>
            )}
            {!s!.rabatt_toepfe && <p className="dezent">Für die Aufteilung nach Rabatten, Maison und Bruch fehlt der Quittungsbericht.</p>}
            {s!.zurueckgenommen_hinweis && (
              <div className="fehler" style={{ marginTop: 12 }}>
                {s!.zurueckgenommen_hinweis}
              </div>
            )}
            {s!.rabattarten.length > 0 && (
              <table style={{ marginTop: 14 }}>
                <thead>
                  <tr><th>Rabattart</th><th className="r">Anzahl</th><th className="r">Summe</th></tr>
                </thead>
                <tbody>
                  {s!.rabattarten.map((r) => (
                    <tr key={r.art}>
                      <td>{r.art}</td>
                      <td className="r zahl dezent">{anzahl(r.anzahl)}</td>
                      <td className="r zahl">{euro(r.summe)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Karte>
    ),
    'tag.debitoren': () => (
      <Kennzahl
        etikett={<>Auf Rechnung <Tipp text="Was an diesem Tag auf Rechnung ging. Die Kasse markiert eine Forderung nicht als bezahlt; der Ausgleich läuft über die Buchhaltung. Die Zahl ist der Zugang dieses Tages, kein Forderungsbestand." /></>}
        wert={d.debitoren!.vorhanden === 0 ? '–' : euro(d.debitoren!.betrag)}
        fuss={d.debitoren!.vorhanden === 0
          ? 'Für diesen Tag liegt kein Tagesabschluss vor.'
          : d.debitoren!.vorhanden < d.debitoren!.erwartet
            ? `gemessen über ${d.debitoren!.vorhanden} von ${d.debitoren!.erwartet} Standorten`
            : undefined} />
    ),
  }

  return (
    <Auswertungsraster kacheln={kacheln} inhalt={inhalt} />
  )
}
